import { createProfileDetails, openProfileEditor } from '../ui/10-profile-editor.mjs';

/** Preserve an explicit verification destination across sign-in; optional setup follows new accounts. */
export function accountDestination(hash, account, referralDestination) {
  if (referralDestination === 'stop-invitations') return referralDestination;
  if (/^#\/verify-email(?:\?|$)/.test(hash)) return hash.slice(2);
  if (account?.verificationRequired) return 'verify-email';
  const onboardingStatus = typeof account === 'string' ? account : account?.onboardingStatus;
  if (onboardingStatus === 'pending') return 'welcome';
  return account?.starterStatus === 'pending' ? 'starter' : (referralDestination ?? 'feed');
}

/** Read a single bounded token from the URL fragment only. Reading never confirms or sends it. */
export function verificationToken(hash) {
  const query = new URLSearchParams(hash.split('?')[1] ?? '');
  const tokens = query.getAll('token');
  return tokens.length === 1 && /^[A-Za-z0-9_-]{43}$/.test(tokens[0]) ? tokens[0] : null;
}

/** Optional setup reuses profile fields/crops and applies only authenticated server snapshots.
 * Private contacts are unverified metadata; skipping setup never bypasses email verification. */
export function createOnboarding(ctx) {
  let owner,
    step = 0,
    draft,
    busy = false;
  const t = (key, fallback) => {
    const value = ctx.translate(`setup.${key}`);
    return value === `setup.${key}` ? fallback : value;
  };
  const destination = () =>
    accountDestination('#/feed', ctx.state.account, ctx.referralDestination?.());
  const active = (actor) => ctx.state?.me.id === actor;
  function initialize() {
    if (owner === ctx.state.me.id) return;
    owner = ctx.state.me.id;
    step = 0;
    draft = {
      ...ctx.state.me,
      profileVisibility: { ...ctx.state.me.profileVisibility },
      phone: ctx.state.account?.phone ?? '',
      secondaryEmail: ctx.state.account?.secondaryEmail ?? '',
    };
    busy = false;
  }
  async function account(path, body) {
    const actor = ctx.state.me.id;
    const snapshot = await ctx.api(path, body);
    if (!active(actor))
      throw new Error(t('sessionChanged', 'Your session changed. Sign in again.'));
    ctx.state = snapshot;
    return snapshot;
  }
  function verificationNotice(host, error, controls, { heading = true, primary = false } = {}) {
    const details = ctx.state.account ?? {};
    if (details.emailVerified) {
      host.append(
        ctx.e('p', 'setup-verified', t('emailVerified', 'Your primary email is verified.')),
      );
      return;
    }
    const notice = ctx.e('section', 'setup-verification');
    if (heading) notice.append(ctx.e('h2', '', t('verifyEmail', 'Verify your email')));
    notice.append(ctx.e('p', 'setup-email', ctx.state.me.email));
    if (details.verificationRequired)
      notice.append(
        ctx.e(
          'p',
          '',
          t(
            'verificationRequired',
            'Verify your email to post, follow people or send messages. Profile setup is optional.',
          ),
        ),
      );
    const states = {
      'not-requested': t('deliveryNotRequested', 'No verification email has been requested yet.'),
      unavailable: t(
        'deliveryUnavailable',
        'Email delivery is unavailable. No verification email has been sent.',
      ),
      queued: t(
        'deliveryQueued',
        'Your verification email is waiting to be sent. Your address is not verified yet.',
      ),
      sent: t(
        'deliverySent',
        'Verification email sent. Open the link, then select Confirm email address.',
      ),
      failed: t('deliveryFailed', 'Email delivery failed. You can try again.'),
    };
    const status = ctx.e('p', 'subtle', states[details.verificationDelivery ?? 'unavailable']);
    status.setAttribute('role', 'status');
    const request = ctx.button(
      t('sendVerification', 'Send verification email'),
      () =>
        run(
          async () => {
            await account('/api/account/verification/request', {});
            if (ctx.route()[0] === 'welcome') renderOnboarding();
            else renderVerification();
          },
          error,
          controls,
        ),
      primary ? 'primary-button' : 'secondary-button',
    );
    controls.push(request);
    notice.append(status, request);
    host.append(notice);
  }
  async function run(action, error, controls) {
    if (busy) return;
    busy = true;
    const disabled = controls.map((control) => control.disabled === true);
    controls.forEach((control) => {
      control.disabled = true;
    });
    error.textContent = '';
    try {
      await action();
    } catch (failure) {
      if (active(owner)) error.textContent = ctx.uiText(failure.message);
    } finally {
      busy = false;
      controls.forEach((control, index) => {
        control.disabled = disabled[index];
      });
    }
  }
  function renderOnboarding() {
    initialize();
    const actor = owner;
    const titles = [
      t('identity', 'Set up your profile'),
      t('about', 'About you'),
      t('contacts', 'Private contact details'),
    ];
    const form = ctx.e('form', 'setup-card form-stack');
    form.dataset.step = String(step);
    const title = ctx.e('h1', '', titles[step]);
    title.tabIndex = -1;
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    const controls = [];
    form.append(
      ctx.e('p', 'eyebrow', `${t('optionalSetup', 'PROFILE · OPTIONAL')} · ${step + 1}/3`),
      title,
    );
    if (ctx.state.account?.verificationRequired) verificationNotice(form, error, controls);
    let details;
    if (step < 2) {
      const keys =
        step === 0 ? ['name', 'handle'] : ['bio', 'location', 'interests', 'pronouns', 'websites'];
      details = createProfileDetails(ctx, keys, draft, (key, value) => {
        draft.profileVisibility[key] = value;
      });
      details.node.className += ' setup-fields';
      for (const [key, field] of details.fields)
        field.addEventListener('input', () => {
          draft[key] = key === 'websites' ? field.value.split('\n') : field.value;
        });
      for (const [key, choice] of details.visibility)
        choice.node.addEventListener('change', () => {
          draft.profileVisibility[key] = choice.value;
        });
      if (step === 0) {
        const identityAvatar = ctx.avatar(ctx.state.me, true);
        identityAvatar.setAttribute('aria-label', ctx.state.me.name);
        form.append(
          ctx.append(
            ctx.e('div', 'setup-identity'),
            identityAvatar,
            ctx.e(
              'p',
              'subtle',
              t('publicIdentity', 'Your display name, handle and profile photo are public.'),
            ),
          ),
        );
        const photos = ctx.button(
          t('editPhotos', 'Edit profile images'),
          () =>
            run(
              async () => {
                await ctx.command({ type: 'profile', ...details.patch() }, { paint: false });
                if (!active(actor) || ctx.route()[0] !== 'welcome') return;
                openProfileEditor(ctx);
                ctx.modal.addEventListener(
                  'close',
                  () => {
                    if (!active(actor)) return;
                    Object.assign(draft, ctx.state.me);
                    draft.profileVisibility = { ...ctx.state.me.profileVisibility };
                    if (ctx.route()[0] === 'welcome') renderOnboarding();
                  },
                  { once: true },
                );
              },
              error,
              controls,
            ),
          'secondary-button',
        );
        controls.push(photos);
        form.append(photos);
      }
      form.append(details.node);
    } else {
      form.append(
        ctx.e(
          'p',
          'subtle',
          t(
            'privateContacts',
            'Only you can see these details. They are not verified or used for sign-in, account recovery or contact matching.',
          ),
        ),
      );
      for (const [key, label, type, maximum, autocomplete] of [
        ['phone', t('phone', 'Phone (optional)'), 'tel', 40, 'tel'],
        [
          'secondaryEmail',
          t('secondaryEmail', 'Secondary email (optional)'),
          'email',
          254,
          'email',
        ],
      ]) {
        const field = ctx.field('input', {
          type,
          value: draft[key] ?? '',
          maxLength: maximum,
          autocomplete,
        });
        field.addEventListener('input', () => {
          draft[key] = field.value;
        });
        form.append(ctx.append(ctx.e('label'), ctx.e('span', '', label), field));
        if (key === 'phone')
          form.append(
            ctx.e(
              'p',
              'setup-field-hint',
              t(
                'phoneFormat',
                'You can leave this blank. Include + and the country code, for example +13125550100.',
              ),
            ),
          );
      }
    }
    const save = ctx.e(
      'button',
      'primary-button',
      step === 2 ? t('finish', 'Save and finish') : t('saveNext', 'Save and continue'),
    );
    save.type = 'submit';
    controls.push(save);
    const back = ctx.button(
      t('back', 'Back'),
      () => {
        if (busy || step === 0) return;
        step--;
        renderOnboarding();
      },
      'secondary-button',
    );
    back.disabled = step === 0;
    controls.push(back);
    const skip = ctx.button(
      t('skipProfile', 'Skip profile setup'),
      () =>
        run(
          async () => {
            await account('/api/account/setup', { action: 'dismiss' });
            if (active(actor)) ctx.go(destination());
          },
          error,
          controls,
        ),
      'text-button',
    );
    controls.push(skip);
    const actions = ctx.append(ctx.e('div', 'setup-actions'), back, skip, save);
    form.append(error, actions);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      return run(
        async () => {
          if (step < 2) {
            await ctx.command({ type: 'profile', ...details.patch() }, { paint: false });
            if (!active(actor) || ctx.route()[0] !== 'welcome') return;
            step++;
            renderOnboarding();
          } else {
            await account('/api/account/setup', {
              phone: draft.phone?.trim().replace(/[\s().-]/g, '') || null,
              secondaryEmail: draft.secondaryEmail?.trim() || null,
              action: 'complete',
            });
            if (active(actor)) ctx.go(destination());
          }
        },
        error,
        controls,
      );
    });
    ctx.view.replaceChildren(form);
    title.focus({ preventScroll: true });
  }
  function renderVerification() {
    initialize();
    const token = verificationToken(location.hash);
    const panel = ctx.e('section', 'setup-card form-stack');
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    const controls = [];
    const verified = ctx.state.account?.emailVerified === true;
    panel.append(
      ctx.e(
        'h1',
        '',
        verified
          ? t('emailVerified', 'Your primary email is verified.')
          : t('verifyEmail', 'Verify your email'),
      ),
    );
    if (!verified && token)
      panel.append(
        ctx.e(
          'p',
          '',
          t(
            'explicitConfirm',
            'Select Confirm email address to verify the address for this account.',
          ),
        ),
      );
    else if (!verified)
      panel.append(
        ctx.e(
          'p',
          '',
          t(
            'checkInbox',
            'Open the link in your email, then select Confirm email address while signed in to this account.',
          ),
        ),
      );
    if (token && !verified) {
      const confirm = ctx.button(
        t('confirmEmail', 'Confirm email address'),
        () =>
          run(
            async () => {
              history.replaceState(null, '', '#/verify-email');
              await account('/api/account/verification/confirm', { token });
              renderVerification();
            },
            error,
            controls,
          ),
        'primary-button',
      );
      controls.push(confirm);
      panel.append(confirm);
    } else if (!verified && location.hash.includes('?'))
      panel.append(
        ctx.e(
          'p',
          'subtle',
          t('missingToken', 'This link is missing or invalid. Request a new verification email.'),
        ),
      );
    if (!verified) {
      verificationNotice(panel, error, controls, { heading: false, primary: !token });
      const refresh = ctx.button(
        t('checkStatus', 'Check verification status'),
        () =>
          run(
            async () => {
              await account('/api/state');
              if (active(owner) && ctx.route()[0] === 'verify-email') renderVerification();
            },
            error,
            controls,
          ),
        'secondary-button',
      );
      const profile = ctx.button(
        t('optionalProfile', 'Edit profile (optional)'),
        () => ctx.go('welcome'),
        'text-button',
      );
      controls.push(refresh, profile);
      panel.append(ctx.append(ctx.e('div', 'setup-verification-actions'), refresh, profile));
    } else {
      const next = ctx.button(
        t('continue', 'Continue'),
        () => ctx.go(destination()),
        'primary-button',
      );
      controls.push(next);
      panel.append(next);
    }
    panel.append(error);
    ctx.view.replaceChildren(panel);
  }
  return { renderOnboarding, renderVerification };
}
